import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, ShoppingBag } from "lucide-react";
import { ErrorNote, cents, goToStripe } from "@/components/connect/shared";

// NOTE: the URL uses the Stripe account ID (/store/acct_...) for this demo.
// In production, switch to a public slug stored in your DB so Stripe IDs aren't exposed.
function ProductCard({ accountId, p }) {
  const [qty, setQty] = useState(1);
  return (
    <div className="flex flex-col rounded-3xl border bg-white p-6" data-testid={`store-product-${p.id}`}>
      <h3 className="text-lg font-semibold">{p.name}</h3>
      <p className="mt-1 flex-1 text-sm text-stone-600">{p.description}</p>
      <p className="mt-4 text-2xl font-bold" data-testid={`store-product-price-${p.id}`}>{cents(p.unit_amount, p.currency || "usd")}</p>
      <div className="mt-4 flex items-center gap-3">
        <Input type="number" min="1" max="20" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} className="w-20" data-testid={`store-qty-${p.id}`} />
        <Button className="flex-1 rounded-full" data-testid={`store-buy-${p.id}`} onClick={() => goToStripe(`/store/${accountId}/checkout`, { product_id: p.id, quantity: qty })}>Buy</Button>
      </div>
    </div>
  );
}

export default function Storefront() {
  const { accountId } = useParams();
  const { data, error, isLoading } = useQuery({ queryKey: ["store", accountId], queryFn: () => api.get(`/store/${accountId}`).then((r) => r.data), retry: false });
  if (isLoading) return <Spinner />;
  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="storefront-page">
      {error && (
        <div className="space-y-4">
          <ErrorNote error={error} testid="storefront-error" />
          <Button asChild variant="outline" className="rounded-full" data-testid="storefront-explore-link"><Link to="/search">Explore stays</Link></Button>
        </div>
      )}
      {data && (
        <>
          <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary"><ShoppingBag className="h-4 w-4" />FairStay host store</p>
          <h1 className="mb-8 mt-2 text-4xl font-bold tracking-tight" data-testid="storefront-name">{data.store_name}</h1>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.products.map((p) => <ProductCard key={p.id} accountId={accountId} p={p} />)}
          </div>
          {data.products.length === 0 && <p className="text-stone-500" data-testid="storefront-empty">This host hasn't added any products yet.</p>}
        </>
      )}
    </div>
  );
}

export function StoreSuccess() {
  const { accountId } = useParams();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { data, error } = useQuery({ queryKey: ["store-session", sid], queryFn: () => api.get(`/store/${accountId}/sessions/${sid}`).then((r) => r.data), enabled: !!sid });
  const paid = data?.payment_status === "paid";
  return (
    <div className="mx-auto mt-20 max-w-md rounded-3xl border bg-white p-10 text-center" data-testid="store-success-page">
      {paid && <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-emerald-600" />}
      <h2 className="text-2xl font-semibold" data-testid="store-success-title">{paid ? "Thanks for your purchase" : "Checking your payment…"}</h2>
      {paid && <p className="mt-2 text-stone-600">You paid {cents(data.amount_total * 100, data.currency)}.</p>}
      {error && <p className="mt-2 text-sm text-red-700">{errMsg(error)}</p>}
      <Button asChild className="mt-6 rounded-full" data-testid="store-back-button"><Link to={`/store/${accountId}`}>Back to the store</Link></Button>
    </div>
  );
}
